import { Component, TemplateRef, ViewChild, OnDestroy, Input, OnInit, Output, EventEmitter } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { MenuItem } from 'primeng/api';

import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { OutletsPublicApi } from '../../../../api/targeting/outlets-public.api';
import { TargetingRhpContextsService } from '../../services/targeting-rhp-contexts.service';
import { ManageListDialogComponent } from '../../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { ContactModel } from '../../model/contact.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import {
    AddPrivateDataDialogComponent
} from '../../../../Discover/Targeting/components/TargetingResults/AddPrivateDataDialog/add-private-data-dialog.component';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { ResearchRequestDialogComponent } from '../../../Dialogs/components/ResearchRequestDialog/research-request-dialog.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService, TargetingAccessLevel } from '../../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { LivenessEnum } from '../../../../api/targeting/enum/liveness.enum';
import { ContactService } from '../../services/contact-service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { EntityDetailsComponent } from '../EntityDetails/entity-details.component';

@Component({
    selector: 'agpr-entity-details-tab',
    templateUrl: './entity-details-tab.component.html',
    styleUrls: [],
    providers: [
        {
            provide: TabComponent,
            useExisting: EntityDetailsTabComponent
        }
    ],
    standalone: false
})
export class EntityDetailsTabComponent extends TabComponent implements OnInit, OnDestroy {

    @Input() entityType: EntityType;
    @Input() expandLabel: string = 'Discover.entityProfile.form.collapse';
    @Input() noOutletMessage: string = 'Discover.entityProfile.noOutlet';
    @Input() noContactMessage: string;
    @Input() syncOutletTabInfo: boolean = true;
    @Input() hiddenNoContentMessage: boolean = false;
    @Input() disableFullProfile: boolean = false;
    @Input() fullHeightProfileTakeover: boolean = false;

    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('tabFooter') declare footer: TemplateRef<any>;
    @ViewChild(EntityDetailsComponent) entityDetails: EntityDetailsComponent;

    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly targetingAccessLevels = TargetingAccessLevel;
    protected subscriptions: Subscription[] = [];

    contact: ContactModel = null;
    outlet: OutletModel;
    privateData: PrivateDataModel;
    currentUser: UserModel;
    actions: MenuItem[] = [];
    contactLoading: boolean = true;
    outletLoading: boolean = true;


    targetingAccessLevel: TargetingAccessLevel;
    showActions: boolean = true;

    constructor(
        public rhp: RightSidebarComponent,
        private coreDialogService: CoreDialogService,
        public outletsPublicApi: OutletsPublicApi,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        public contactService: ContactService
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.targetingRhpContextsService.getContact().subscribe(contact => {
                this.contact = contact;

                if (contact) {
                    this.actions = [
                        {
                            label: this.localizationService.transform('Discover.manageList.header'),
                            command: () => this.openManageListDialog()
                        }
                    ];
                    if (!contact.privateContactMetadata) {
                        this.actions.unshift({
                            label: this.localizationService.transform('Discover.addPrivateData.buttonLabel'),
                            command: () => this.openAddPrivateDataDialog()
                        },
                        {
                            label: this.localizationService.transform('Discover.research.researchRequest'),
                            command: () => this.openResearchRequestDialog()
                        });
                    }
                }

                if (this.entityType === EntityType.CONTACT) {
                    this.showActions = this.contact && !this.contact.isGeoRestricted;
                }
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(outlet => {
                this.outlet = outlet.outlet;
                if (outlet?.outlet) {
                    this.actions = [
                        {
                            label: this.localizationService.transform('Discover.manageList.header'),
                            command: () => this.openManageListDialog()
                        }
                    ];
                    if ((this.entityType === EntityType.CONTACT && !(this.contact?.privateContactMetadata))
                        || (this.entityType === EntityType.OUTLET && this.outlet.id && this.outlet.liveness === LivenessEnum.ACTIVE)) {
                        this.actions.unshift({
                            label: this.localizationService.transform('Discover.addPrivateData.buttonLabel'),
                            command: () => this.openAddPrivateDataDialog()
                        },
                        {
                            label: this.localizationService.transform('Discover.research.researchRequest'),
                            command: () => this.openResearchRequestDialog()
                        });
                    }
                } else if (this.entityType === EntityType.OUTLET) {
                    this.actions = null;
                }

                if (this.entityType === EntityType.OUTLET) {
                    this.showActions = this.outlet && !this.outlet.isGeoRestricted;
                }
            }),
            this.targetingRhpContextsService.getContactPrivateData().subscribe(privateData => {
                if (this.entityType === EntityType.CONTACT) {
                    this.privateData = privateData;
                }
            }),
            this.targetingRhpContextsService.getOutletPrivateData().subscribe(privateData => {
                if (this.entityType === EntityType.OUTLET) {
                    this.privateData = privateData;
                }
            }),
            this.targetingRhpContextsService.getContactLoading().subscribe(loading => {
                this.contactLoading = loading;
                if (this.entityType === EntityType.CONTACT) {
                    this.loading.emit(loading);
                }
            }),
            this.targetingRhpContextsService.getOutletLoading().subscribe(loading => {
                this.outletLoading = loading;
                if (this.entityType === EntityType.OUTLET) {
                    this.loading.emit(loading);
                }
            }),
            this.currentUserStateService.getTargetingAccessLevel().subscribe(x => this.targetingAccessLevel = x),
            this.currentUserStateService.getUser().subscribe(x => this.currentUser = x),
            this.contactService.getPrivateDataAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (this.entityType === EntityType.CONTACT && update.entityType === EntityType.CONTACT && update.entityId === this.contact?.id) {
                    this.privateData = update.privateData;
                } else if (this.entityType === EntityType.OUTLET && update.entityType === EntityType.OUTLET && update.entityId === this.outlet?.id) {
                    this.privateData = update.privateData;
                }
            })
        );
    }

    openAddPrivateDataDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');
        this.coreDialogService.open(AddPrivateDataDialogComponent, {
            inputs: {
                setEntity: (this.entityType === EntityType.CONTACT ? this.contact : this.outlet),
                setPrivateData: this.privateData,
                currentUser: this.currentUser,
                overlayContainer: overlayContainer,
                animationTrigger: animationTrigger
            },
            outputs: {}
        });
    }

    openResearchRequestDialog() {
        const inputs = this.entityType === EntityType.CONTACT
            ? { contact: this.contact }
            : { outlet: this.outlet };
        this.coreDialogService.open(ResearchRequestDialogComponent, { inputs: inputs });
    }

    openManageListDialog() {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: (this.entityType === EntityType.CONTACT ? this.contact : this.outlet)
            },
            outputs: {
            }
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
